import { EMPTY, merge, Observable, of, SchedulerLike } from 'rxjs';
import { catchError, map, observeOn, tap } from 'rxjs/operators';
import { CachedPage, PaginationCache } from './pagination-cache';

export interface SwrQueryConfig<T, R> {
  /** Cache instance owned by the feature (e.g. one per NgRx effects class). */
  cache: PaginationCache<T>;
  /** Stable key for this query; build it with {@link PaginationCache.buildKey}. */
  key: string;
  /** Fetches a fresh page from the server. Only called on a cold miss or when stale. */
  fetch: () => Observable<CachedPage<T>>;
  /**
   * Maps a page (cached or freshly fetched) into the value to emit — typically an
   * NgRx success action.
   */
  onSuccess: (page: CachedPage<T>) => R;
  /**
   * Optional side effect run for every served page (cached hit and background
   * revalidation alike), e.g. drawer navigation. Not run on cold-miss errors.
   */
  onServe?: (page: CachedPage<T>) => void;
  /** Handles a cold-miss fetch error — e.g. show a toast and emit a failure action. */
  onError: (error: unknown) => Observable<R>;
  serveScheduler?: SchedulerLike;
}

/**
 * Stale-while-revalidate query
 *
 * - **Cold miss**: fetch, cache the result, emit it. Errors flow through `onError`
 *   (spinner/error toast path).
 * - **Fresh hit** (age <= `staleTime`): emit the cached page instantly, no network.
 * - **Stale hit** (age > `staleTime`): emit the cached page instantly, then silently
 *   revalidate in the background (no spinner, errors swallowed) and emit the fresh page.
 *
 * Reusable across any paged list feature: instantiate a {@link PaginationCache} in the
 * feature's effects, build a key from the request query, and delegate the load to this.
 */
export function swrQuery<T, R>(config: SwrQueryConfig<T, R>): Observable<R> {
  const { cache, key, fetch, onSuccess, onServe, onError, serveScheduler } =
    config;

  const fetch$ = fetch().pipe(tap(page => cache.set(key, page)));

  const cached = cache.get(key);
  if (cached) {
    onServe?.(cached.page);
    const served = of(onSuccess(cached.page));
    const cached$ = serveScheduler
      ? served.pipe(observeOn(serveScheduler))
      : served;
    if (!cached.isStale) {
      return cached$;
    }

    const revalidate$ = fetch$.pipe(
      tap(page => onServe?.(page)),
      map(onSuccess),
      catchError(() => EMPTY)
    );
    return merge(cached$, revalidate$);
  }

  return fetch$.pipe(
    tap(page => onServe?.(page)),
    map(onSuccess),
    catchError(onError)
  );
}
